/**
 * Typed API client for the Access module.
 *
 * Every function corresponds 1:1 to a backend endpoint in
 * backend/src/modules/access/routes/access-routes.ts. Keep them in sync:
 * changes to the endpoint shape should be accompanied by a change here.
 */

import { apiRequest } from './client';
import type {
  CreateUserInput,
  UpdateUserInput,
  CreateRoleInput,
  UpdateRoleInput,
  Paginated,
} from '@loanaudit/shared';

export interface UserListItem {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  roles: string[];
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  key: string;
  name: string;
  description: string;
  permissions: string[];
  userCount: number;
  isBuiltin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  key: string;
  module: string;
  action: string;
  description: string;
}

export interface PageRegistryEntry {
  path: string;
  title: string;
  module: string;
  requiredPermission: string;
  registeredAt: string;
  lastSeenAt: string;
  isActive: boolean;
}

export const accessApi = {
  listUsers(params: { page: number; pageSize: number; search?: string }) {
    return apiRequest<Paginated<UserListItem>>('/api/access/users', {
      query: params,
    });
  },
  createUser(body: CreateUserInput) {
    return apiRequest<{ id: string }>('/api/access/users', { method: 'POST', body });
  },
  updateUser(id: string, body: UpdateUserInput) {
    return apiRequest<{ updated: true }>(`/api/access/users/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  listRoles() {
    return apiRequest<Role[]>('/api/access/roles');
  },
  createRole(body: CreateRoleInput) {
    return apiRequest<{ id: string; key: string }>('/api/access/roles', {
      method: 'POST',
      body,
    });
  },
  updateRole(key: string, body: UpdateRoleInput) {
    return apiRequest<{ updated: true }>(`/api/access/roles/${key}`, {
      method: 'PATCH',
      body,
    });
  },

  listPermissions() {
    return apiRequest<Permission[]>('/api/access/permissions');
  },

  listPages() {
    return apiRequest<PageRegistryEntry[]>('/api/access/pages');
  },
};
